import type { ReloadOptions } from "@inertiaui/modal-react";
import { t } from "@travily/i18n";
import type FriendRow from "@travily/types/models/ProfileFriendshipsRow";
import { showToast } from "@ui/components/Toast";
import { useDebouncedCallback } from "@ui/hooks/use-debounced-callback";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useCsrfToken } from "@/hooks/use-csrf-token";
import { type FriendshipMutation, submitFriendshipMutation } from "@/lib/friendship-actions";
import type { FriendshipTab } from "../pages/friendships/show.view";

interface FriendsPagy {
  page: number;
  next: number | null;
}

type RowPatch = Partial<Pick<FriendRow, "friendship_status" | "friendship_id">>;

interface UseFriendshipsModalArgs {
  /** The page of rows from the modal's props (replaced on every reload). */
  friends: FriendRow[];
  /** The countless cursor for that page. */
  friendsPagy: FriendsPagy;
  /** @inertiaui modal reload — a partial reload of this modal's own props. */
  reload: (options?: ReloadOptions) => void;
}

/**
 * Owns the friends-list modal's data. The list lives in the modal's Inertia props;
 * search (debounced), tab switches and pagination all reload this one endpoint via
 * the modal's `reload`. That reload *replaces* props rather than merging (the modal
 * doesn't run Inertia's scroll/merge machinery), so pages are accumulated here — a
 * `mode` ref marks each incoming props update as a replace (search/tab) or an append
 * (load more). Row mutations stay optimistic via a local override map layered over
 * the accumulated rows, since the write endpoints navigate and would close the modal.
 */
export function useFriendshipsModal({ friends: propFriends, friendsPagy: propPagy, reload }: UseFriendshipsModalArgs) {
  const csrfToken = useCsrfToken();
  const [tab, setTab] = useState<FriendshipTab>("active");
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const [friends, setFriends] = useState<FriendRow[]>(propFriends);
  // The query the current `friends` reflect. Empty/no-results UI keys off this, not the
  // live input, so clearing a no-results search doesn't flash the empty state during the
  // debounce gap before the unfiltered list arrives.
  const [loadedQuery, setLoadedQuery] = useState("");
  const [nextPage, setNextPage] = useState<number | null>(propPagy.next);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isError, setIsError] = useState(false);
  const [processingId, setProcessingId] = useState<number | null>(null);
  // Optimistic per-row patches, layered over the accumulated rows at render. Cleared
  // when a replace load lands (the server response is then the source of truth).
  const [overrides, setOverrides] = useState<Record<number, RowPatch>>({});

  const modeRef = useRef<"replace" | "append">("replace");
  // The query of the in-flight reload, applied to `loadedQuery` when its props land.
  const pendingQueryRef = useRef("");
  // The last props.friends we folded in. Seeded with the initial page so the mount
  // pass (and React's strict-mode double-invoke) is a no-op.
  const appliedRef = useRef(propFriends);

  useEffect(() => {
    if (appliedRef.current === propFriends) return;
    appliedRef.current = propFriends;
    setNextPage(propPagy.next);
    if (modeRef.current === "append") {
      setFriends((prev) => [...prev, ...propFriends]);
    } else {
      setFriends(propFriends);
      setLoadedQuery(pendingQueryRef.current);
    }
  }, [propFriends, propPagy]);

  const reloadList = useCallback(
    (q: string, scope: FriendshipTab) => {
      modeRef.current = "replace";
      pendingQueryRef.current = q;
      setOverrides({});
      reload({
        only: ["friends", "friends_pagy"],
        data: { q, scope },
        onStart: () => {
          setIsError(false);
          setIsLoading(true);
        },
        onError: () => setIsError(true),
        onFinish: () => setIsLoading(false),
      });
    },
    [reload],
  );

  // Reload page 1 when the scope or debounced query changes. The first run is skipped
  // — the modal props already hold page 1 for the default scope and empty query.
  const firstRun = useRef(true);
  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    reloadList(debouncedQuery, tab);
  }, [tab, debouncedQuery, reloadList]);

  const pushDebouncedQuery = useDebouncedCallback((q: string) => setDebouncedQuery(q), 300);
  const onQueryChange = useCallback(
    (value: string) => {
      setQuery(value);
      pushDebouncedQuery(value);
    },
    [pushDebouncedQuery],
  );

  // Params for the @inertiaui WhenVisible sentinel the page renders while `hasMore`.
  const loadMoreParams = useMemo<ReloadOptions>(
    () => ({
      only: ["friends", "friends_pagy"],
      data: { q: debouncedQuery, scope: tab, page: nextPage },
      onStart: () => {
        modeRef.current = "append";
        setIsError(false);
        setIsLoadingMore(true);
      },
      onError: () => setIsError(true),
      onFinish: () => setIsLoadingMore(false),
    }),
    [debouncedQuery, tab, nextPage],
  );

  const onRetry = useCallback(() => reloadList(debouncedQuery, tab), [reloadList, debouncedQuery, tab]);

  const runMutation = useCallback(
    async (friend: FriendRow, mutation: FriendshipMutation, optimistic: RowPatch) => {
      const { id } = friend;
      setProcessingId(id);
      setOverrides((current) => ({ ...current, [id]: { ...current[id], ...optimistic } }));

      const ok = await submitFriendshipMutation({
        mutation,
        handle: friend.handle,
        friendshipId: friend.friendship_id,
        csrfToken,
      });

      setProcessingId((current) => (current === id ? null : current));
      if (!ok) {
        setOverrides((current) => {
          const next = { ...current };
          delete next[id];
          return next;
        });
        // i18n-tasks-use t('common.friendships.action_error.title')
        // i18n-tasks-use t('common.friendships.action_error.description')
        showToast.error(t("common.friendships.action_error.title"), t("common.friendships.action_error.description"));
      }
    },
    [csrfToken],
  );

  const onBecomeFriends = useCallback(
    (friend: FriendRow) => {
      if (friend.friendship_status !== "none") return;
      runMutation(friend, "create", { friendship_status: "pending_sent" });
    },
    [runMutation],
  );

  const onAccept = useCallback(
    (friend: FriendRow) => runMutation(friend, "accept", { friendship_status: "accepted" }),
    [runMutation],
  );

  const onDecline = useCallback(
    (friend: FriendRow) => runMutation(friend, "decline", { friendship_status: "none", friendship_id: null }),
    [runMutation],
  );

  const onUnfriend = useCallback(
    (friend: FriendRow) => runMutation(friend, "unfriend", { friendship_status: "none", friendship_id: null }),
    [runMutation],
  );

  const displayFriends = useMemo(
    () => friends.map((friend) => (overrides[friend.id] ? { ...friend, ...overrides[friend.id] } : friend)),
    [friends, overrides],
  );

  return {
    tab,
    onTabChange: setTab,
    query,
    onQueryChange,
    friends: displayFriends,
    loadedQuery,
    isLoading,
    isLoadingMore,
    isError,
    hasMore: nextPage != null,
    loadMoreParams,
    onRetry,
    processingId,
    onBecomeFriends,
    onAccept,
    onDecline,
    onUnfriend,
  };
}
